import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { UseFormWatch } from 'react-hook-form';

import Button from '@/components/Button';
import KitThumbnailStack from '@/sections/Fulfill/Inventory/Table/KitThumbnailStack';
import { Product } from '@/types/fulfill/inventory';
import { formatCurrency } from '@/utils/common';

import { CreateKitFormData } from '../schema';

interface KitComponentField {
  id: string;
  sku: string;
  quantity: number;
}

interface CreateKitStepSummaryProps {
  name?: string;
  sku?: string;
  price?: number;
  fields: KitComponentField[];
  selectedProductsMap: Map<string, Product>;
  watch: UseFormWatch<CreateKitFormData>;
  onSubmit: () => void;
  isCreating: boolean;
}

const CreateKitStepSummary = (props: CreateKitStepSummaryProps) => {
  const theme = useTheme();
  const liveComponents = props.watch('components') ?? [];
  const totalPieces = liveComponents.reduce(
    (sum, component) => sum + (component?.quantity || 0),
    0,
  );
  const kitThumbnailComponents = props.fields.map((field) => {
    const product = props.selectedProductsMap.get(field.sku);
    return {
      sku: field.sku,
      name: product?.name,
      thumbnail: product?.thumbnail,
      quantity: field.quantity,
    };
  });

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 2 }}>
        Revisa la composición del Kit antes de crearlo. Una vez creado, no podrás modificar sus
        componentes.
      </Typography>

      <Box
        sx={{
          border: '1px solid',
          borderColor: theme.palette.grey[300],
          borderRadius: '1rem',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            p: 2,
            backgroundColor: theme.palette.primary.lighter,
            borderBottom: '1px solid',
            borderColor: theme.palette.grey[300],
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <KitThumbnailStack components={kitThumbnailComponents} />
            <Box>
              <Typography variant="body1" fontWeight="bold">
                {props.name}
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.grey[600] }}>
                SKU {props.sku}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
            <Typography variant="body2" sx={{ color: theme.palette.grey[600] }}>
              Costo
            </Typography>
            <Typography
              variant="body1"
              fontWeight="bold"
              sx={{ color: theme.palette.primary.main }}
            >
              {formatCurrency(props.price)}
            </Typography>
          </Box>
        </Box>

        {props.fields.map((field, index) => {
          const product = props.selectedProductsMap.get(field.sku);
          return (
            <Box key={field.id}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                  py: 1.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <img
                    src={product?.thumbnail}
                    alt={product?.name}
                    style={{ width: 44, height: 44, borderRadius: '0.75rem', objectFit: 'cover' }}
                  />
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {product?.name ?? field.sku}
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.grey[600] }}>
                      SKU {field.sku}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" fontWeight="bold" color={theme.palette.primary.main}>
                  {liveComponents[index]?.quantity ?? field.quantity}{' '}
                  {(liveComponents[index]?.quantity ?? field.quantity) === 1 ? 'pieza' : 'piezas'}
                </Typography>
              </Box>
              {index < props.fields.length - 1 && <Divider sx={{ mx: 2 }} />}
            </Box>
          );
        })}
      </Box>

      <Grid container justifyContent="space-between" alignItems="center" mt={2}>
        <Typography variant="body2" fontWeight="bold">
          Total de piezas: {totalPieces}
        </Typography>
        <Button onClick={props.onSubmit} disabled={props.isCreating} sx={{ minWidth: '120px' }}>
          {props.isCreating ? <CircularProgress size={20} color="primary" /> : 'Crear Kit'}
        </Button>
      </Grid>
    </Box>
  );
};

export default CreateKitStepSummary;
